import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import Header from "@/components/Header";
import CookieConsent from "@/components/CookieConsent";
import PageViewTracker from "@/components/PageViewTracker";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";

// Eagerly load main index pages for fast LCP
import Index from "./pages/Index";
import IndexEN from "./pages/en/Index";
import IndexDE from "./pages/de/Index";
import IndexPT from "./pages/pt/Index";
import IndexFR from "./pages/fr/Index";

// Lazy load all other pages
const InvestigacionProyectos = lazy(() => import("./pages/InvestigacionProyectos"));
const EnsayosClinicos = lazy(() => import("./pages/EnsayosClinicos"));
const Colaboraciones = lazy(() => import("./pages/Colaboraciones"));
const Eventos = lazy(() => import("./pages/Eventos"));
const Contacto = lazy(() => import("./pages/Contacto"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const DonationSuccess = lazy(() => import("./pages/DonationSuccess"));
const Voluntariado = lazy(() => import("./pages/Voluntariado"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacidad = lazy(() => import("./pages/Privacidad"));
const Terminos = lazy(() => import("./pages/Terminos"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminContacts = lazy(() => import("./pages/admin/Contacts"));
const AdminNewsletter = lazy(() => import("./pages/admin/NewsletterSubs"));
const AdminVisits = lazy(() => import("./pages/admin/ScheduledVisits"));
const AdminStatistics = lazy(() => import("./pages/admin/Statistics"));

// English pages
const ContactEN = lazy(() => import("./pages/en/Contact"));
const CollaborationsEN = lazy(() => import("./pages/en/Collaborations"));
const ClinicalTrialsEN = lazy(() => import("./pages/en/ClinicalTrials"));
const EventsEN = lazy(() => import("./pages/en/Events"));
const ResearchProjectsEN = lazy(() => import("./pages/en/ResearchProjects"));
const NewsletterEN = lazy(() => import("./pages/en/Newsletter"));
const VolunteeringEN = lazy(() => import("./pages/en/Volunteering"));
const DonationSuccessEN = lazy(() => import("./pages/en/DonationSuccess"));
const Privacy = lazy(() => import("./pages/en/Privacy"));
const Terms = lazy(() => import("./pages/en/Terms"));

// German pages
const ContactDE = lazy(() => import("./pages/de/Contact"));
const CollaborationsDE = lazy(() => import("./pages/de/Collaborations"));
const ClinicalTrialsDE = lazy(() => import("./pages/de/ClinicalTrials"));
const EventsDE = lazy(() => import("./pages/de/Events"));
const ResearchProjectsDE = lazy(() => import("./pages/de/ResearchProjects"));
const NewsletterDE = lazy(() => import("./pages/de/Newsletter"));
const VolunteeringDE = lazy(() => import("./pages/de/Volunteering"));
const DonationSuccessDE = lazy(() => import("./pages/de/DonationSuccess"));
const Datenschutz = lazy(() => import("./pages/de/Datenschutz"));
const Nutzungsbedingungen = lazy(() => import("./pages/de/Nutzungsbedingungen"));

// Portuguese pages
const ContactPT = lazy(() => import("./pages/pt/Contact"));
const CollaborationsPT = lazy(() => import("./pages/pt/Collaborations"));
const ClinicalTrialsPT = lazy(() => import("./pages/pt/ClinicalTrials"));
const EventsPT = lazy(() => import("./pages/pt/Events"));
const ResearchProjectsPT = lazy(() => import("./pages/pt/ResearchProjects"));
const NewsletterPT = lazy(() => import("./pages/pt/Newsletter"));
const VolunteeringPT = lazy(() => import("./pages/pt/Volunteering"));
const DonationSuccessPT = lazy(() => import("./pages/pt/DonationSuccess"));
const Privacidade = lazy(() => import("./pages/pt/Privacidade"));
const TermosDeUso = lazy(() => import("./pages/pt/TermosDeUso"));

// French pages
const ContactFR = lazy(() => import("./pages/fr/Contact"));
const CollaborationsFR = lazy(() => import("./pages/fr/Collaborations"));
const ClinicalTrialsFR = lazy(() => import("./pages/fr/ClinicalTrials"));
const EventsFR = lazy(() => import("./pages/fr/Events"));
const ResearchProjectsFR = lazy(() => import("./pages/fr/ResearchProjects"));
const NewsletterFR = lazy(() => import("./pages/fr/Newsletter"));
const VolunteeringFR = lazy(() => import("./pages/fr/Volunteering"));
const DonationSuccessFR = lazy(() => import("./pages/fr/DonationSuccess"));
const PrivacyFR = lazy(() => import("./pages/fr/Privacy"));
const ConditionsUtilisation = lazy(() => import("./pages/fr/ConditionsUtilisation"));

const queryClient = new QueryClient();

// Layout wrapper for public pages (with Header)
const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    <Header />
    <main>{children}</main>
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <CookieConsent />
            <PageViewTracker />
            <div className="min-h-screen bg-background">
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="contacts" element={<AdminContacts />} />
                    <Route path="newsletter" element={<AdminNewsletter />} />
                    <Route path="visits" element={<AdminVisits />} />
                    <Route path="statistics" element={<AdminStatistics />} />
                  </Route>

                  {/* Public routes */}
                  <Route path="/" element={<PublicLayout><Index /></PublicLayout>} />
                  <Route path="/investigacion-proyectos" element={<PublicLayout><InvestigacionProyectos /></PublicLayout>} />
                  <Route path="/ensayos-clinicos" element={<PublicLayout><EnsayosClinicos /></PublicLayout>} />
                  <Route path="/colaboraciones" element={<PublicLayout><Colaboraciones /></PublicLayout>} />
                  <Route path="/eventos" element={<PublicLayout><Eventos /></PublicLayout>} />
                  <Route path="/contacto" element={<PublicLayout><Contacto /></PublicLayout>} />
                  <Route path="/voluntariado" element={<PublicLayout><Voluntariado /></PublicLayout>} />
                  <Route path="/newsletter" element={<PublicLayout><Newsletter /></PublicLayout>} />
                  <Route path="/donation-success" element={<PublicLayout><DonationSuccess /></PublicLayout>} />
                  <Route path="/privacidad" element={<PublicLayout><Privacidad /></PublicLayout>} />
                  <Route path="/terminos" element={<PublicLayout><Terminos /></PublicLayout>} />

                  {/* English routes */}
                  <Route path="/en" element={<PublicLayout><IndexEN /></PublicLayout>} />
                  <Route path="/en/research-projects" element={<PublicLayout><ResearchProjectsEN /></PublicLayout>} />
                  <Route path="/en/clinical-trials" element={<PublicLayout><ClinicalTrialsEN /></PublicLayout>} />
                  <Route path="/en/collaborations" element={<PublicLayout><CollaborationsEN /></PublicLayout>} />
                  <Route path="/en/events" element={<PublicLayout><EventsEN /></PublicLayout>} />
                  <Route path="/en/contact" element={<PublicLayout><ContactEN /></PublicLayout>} />
                  <Route path="/en/volunteering" element={<PublicLayout><VolunteeringEN /></PublicLayout>} />
                  <Route path="/en/newsletter" element={<PublicLayout><NewsletterEN /></PublicLayout>} />
                  <Route path="/en/donation-success" element={<PublicLayout><DonationSuccessEN /></PublicLayout>} />
                  <Route path="/en/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
                  <Route path="/en/terms" element={<PublicLayout><Terms /></PublicLayout>} />

                  {/* German routes */}
                  <Route path="/de" element={<PublicLayout><IndexDE /></PublicLayout>} />
                  <Route path="/de/forschungsprojekte" element={<PublicLayout><ResearchProjectsDE /></PublicLayout>} />
                  <Route path="/de/klinische-studien" element={<PublicLayout><ClinicalTrialsDE /></PublicLayout>} />
                  <Route path="/de/zusammenarbeit" element={<PublicLayout><CollaborationsDE /></PublicLayout>} />
                  <Route path="/de/veranstaltungen" element={<PublicLayout><EventsDE /></PublicLayout>} />
                  <Route path="/de/kontakt" element={<PublicLayout><ContactDE /></PublicLayout>} />
                  <Route path="/de/freiwilligenarbeit" element={<PublicLayout><VolunteeringDE /></PublicLayout>} />
                  <Route path="/de/newsletter" element={<PublicLayout><NewsletterDE /></PublicLayout>} />
                  <Route path="/de/donation-success" element={<PublicLayout><DonationSuccessDE /></PublicLayout>} />
                  <Route path="/de/datenschutz" element={<PublicLayout><Datenschutz /></PublicLayout>} />
                  <Route path="/de/nutzungsbedingungen" element={<PublicLayout><Nutzungsbedingungen /></PublicLayout>} />

                  {/* Portuguese routes */}
                  <Route path="/pt" element={<PublicLayout><IndexPT /></PublicLayout>} />
                  <Route path="/pt/projetos-pesquisa" element={<PublicLayout><ResearchProjectsPT /></PublicLayout>} />
                  <Route path="/pt/ensaios-clinicos" element={<PublicLayout><ClinicalTrialsPT /></PublicLayout>} />
                  <Route path="/pt/colaboracoes" element={<PublicLayout><CollaborationsPT /></PublicLayout>} />
                  <Route path="/pt/eventos" element={<PublicLayout><EventsPT /></PublicLayout>} />
                  <Route path="/pt/contato" element={<PublicLayout><ContactPT /></PublicLayout>} />
                  <Route path="/pt/voluntariado" element={<PublicLayout><VolunteeringPT /></PublicLayout>} />
                  <Route path="/pt/newsletter" element={<PublicLayout><NewsletterPT /></PublicLayout>} />
                  <Route path="/pt/donation-success" element={<PublicLayout><DonationSuccessPT /></PublicLayout>} />
                  <Route path="/pt/privacidade" element={<PublicLayout><Privacidade /></PublicLayout>} />
                  <Route path="/pt/termos-de-uso" element={<PublicLayout><TermosDeUso /></PublicLayout>} />

                  {/* French routes */}
                  <Route path="/fr" element={<PublicLayout><IndexFR /></PublicLayout>} />
                  <Route path="/fr/projets-recherche" element={<PublicLayout><ResearchProjectsFR /></PublicLayout>} />
                  <Route path="/fr/essais-cliniques" element={<PublicLayout><ClinicalTrialsFR /></PublicLayout>} />
                  <Route path="/fr/collaborations" element={<PublicLayout><CollaborationsFR /></PublicLayout>} />
                  <Route path="/fr/evenements" element={<PublicLayout><EventsFR /></PublicLayout>} />
                  <Route path="/fr/contact" element={<PublicLayout><ContactFR /></PublicLayout>} />
                  <Route path="/fr/benevolat" element={<PublicLayout><VolunteeringFR /></PublicLayout>} />
                  <Route path="/fr/newsletter" element={<PublicLayout><NewsletterFR /></PublicLayout>} />
                  <Route path="/fr/donation-success" element={<PublicLayout><DonationSuccessFR /></PublicLayout>} />
                  <Route path="/fr/privacy" element={<PublicLayout><PrivacyFR /></PublicLayout>} />
                  <Route path="/fr/conditions-utilisation" element={<PublicLayout><ConditionsUtilisation /></PublicLayout>} />

                  <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
                </Routes>
              </Suspense>
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
